import { useGlobalAdmin } from '@/hooks/use-global-admin';
import { FEATURE_FLAGS, useFeatureFlag } from '@/lib/feature-flags';
import {
  BeaconLogo,
  Eyebrow,
  Icon,
  RadarMark,
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipTrigger,
} from '@beacon/ui/components';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { SidebarAds } from './SidebarAds';
import { useSidebar } from './SidebarContext';
import { SidebarHelp } from './SidebarHelp';
import { SidebarNavLink } from './SidebarNavLink';
import { SidebarOrgSwitcher } from './SidebarOrgSwitcher';
import { SidebarRecentItems } from './SidebarRecentItems';
import { SidebarUserMenu } from './SidebarUserMenu';

function useSyncTimestamp() {
  const [ts, setTs] = useState(() => formatTime(new Date()));

  useEffect(() => {
    const id = setInterval(() => setTs(formatTime(new Date())), 30_000);

    return () => clearInterval(id);
  }, []);
  return ts;
}

function formatTime(d: Date) {
  return d.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export function Sidebar() {
  const lastSync = useSyncTimestamp();
  const { isGlobalAdmin } = useGlobalAdmin();
  const isBeaconStaff = useFeatureFlag(FEATURE_FLAGS.IS_BEACON_STAFF);
  const { collapsed, toggle } = useSidebar();

  return (
    <aside
      className={[
        'border-stroke-1 bg-surface-1 fixed inset-y-0 left-0 z-30 flex flex-col border-r transition-[width] duration-200',
        collapsed ? 'w-[60px]' : 'w-[220px]',
      ].join(' ')}
    >
      <div
        className={[
          'border-stroke-1 flex h-14 shrink-0 items-center border-b',
          collapsed ? 'justify-center px-2' : 'px-[18px]',
        ].join(' ')}
      >
        <Link to="/" className="flex min-w-0 items-center" aria-label="BEACON home">
          {collapsed ? <RadarMark size={22} /> : <BeaconLogo />}
        </Link>

        {!collapsed && (
          <button
            onClick={toggle}
            aria-label="Collapse sidebar"
            className="text-text-3 hover:bg-surface-2 hover:text-text-1 ml-auto grid h-[26px] w-[26px] shrink-0 place-items-center rounded transition-colors"
          >
            <Icon symbol="sidebar-fold-line" className="h-4 w-4" />
          </button>
        )}
      </div>

      {collapsed && (
        <div className="flex justify-center pt-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={toggle}
                aria-label="Expand sidebar"
                className="text-text-3 border-stroke-2 grid h-[34px] w-[38px] place-items-center rounded border border-dashed transition-colors"
              >
                <Icon symbol="sidebar-unfold-line" className="h-[15px] w-[15px]" />
              </button>
            </TooltipTrigger>
            <TooltipPortal>
              <TooltipContent side="right">Expand sidebar</TooltipContent>
            </TooltipPortal>
          </Tooltip>
        </div>
      )}

      <SidebarOrgSwitcher collapsed={collapsed} />

      {collapsed && <div className="bg-stroke-1 mx-auto h-px w-7" />}

      <nav
        className={[
          'scroll flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto',
          collapsed ? 'px-1.5 pt-3' : 'p-2.5',
        ].join(' ')}
      >
        {!collapsed && <Eyebrow className="text-3xs px-2.5 pt-2 pb-1.5">Modules</Eyebrow>}

        <div className={collapsed ? 'flex w-full flex-col items-center gap-1' : ''}>
          <SidebarNavLink to="/" icon="dashboard-line" label="Dashboard" code="00" end collapsed={collapsed} />
          {isBeaconStaff && (
            <>
              <SidebarNavLink to="/candidates" icon="user-line" label="Candidates" code="02" collapsed={collapsed} />
            </>
          )}
        </div>

        {!collapsed && (
          <div className="mt-4 space-y-3">
            <SidebarRecentItems title="Recent Jobs" storageKey="beacon:recent-jobs" basePath="/jobs" />
            {isBeaconStaff && (
              <SidebarRecentItems
                title="Recent Candidates"
                storageKey="beacon:recent-candidates"
                basePath="/candidates"
              />
            )}
          </div>
        )}

        {isGlobalAdmin && (
          <>
            {collapsed ? (
              <div className="bg-stroke-1 mx-2 my-2 h-px" />
            ) : (
              <Eyebrow className="text-3xs px-2.5 pt-4 pb-1.5">Platform</Eyebrow>
            )}
            <SidebarNavLink to="/admin" icon="list-settings-line" label="Admin" code="GA" collapsed={collapsed} />
          </>
        )}

        <div className="flex-1" />
      </nav>

      <SidebarAds collapsed={collapsed} />

      <div className="border-stroke-1 border-t p-2">
        <SidebarHelp collapsed={collapsed} />
      </div>

      <div className="border-stroke-1 border-t p-2">
        <SidebarUserMenu collapsed={collapsed} />
      </div>
    </aside>
  );
}
